import { Injectable, computed } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';

import { EasTable, EasTableDefinition, TableSource } from '@fry/lib/store';
import { ListDefaults, SearchFilter, SearchModel, SearchSelect, SearchSort, TablePart, TableRequest } from '@fry/lib/list/search.interface';
import { cloneDeep  } from 'lodash';
import { EASPersonalStorageService } from '@fry/lib/users';
import { toSignal } from '@angular/core/rxjs-interop';
import { EasBuilderField } from '@fry/components/common/form-builder/builder.interfaces';

type ListStateType = 'pending' |
                            'empty' |
                            'full' |
                            'loading' |
                            'error';
interface ListState {
  type: ListStateType;
  error?: Error;
}

export interface AllowedFilters {
  featured: EasBuilderField[];
  normal: EasBuilderField[];
  quick: EasBuilderField[];
}

const ListStatePending: ListState = { type: 'pending' };
const ListStateEmpty: ListState = { type: 'empty' };
const ListStateFull: ListState = { type: 'full' };
const ListStateLoading: ListState = { type: 'loading' };

@Injectable()
export class TableService {

  private _table = new BehaviorSubject<EasTable>({columns: [], data: []});
  public readonly table = this._table.asObservable();

  private _tableDefinition = new BehaviorSubject<EasTableDefinition | null>(null);
  public readonly EasTableDefinition = this._tableDefinition.asObservable().pipe(
    filter(def => def !== null),
    map(def => {
      if (def === null) {
        throw new Error('Hey, there is a filter')
      }
      return def;
    })
  )

  private _currentSearchModel$ = new BehaviorSubject<SearchModel>({ filter: {}, sort: {}});
  public readonly currentSearchModel$ = this._currentSearchModel$.asObservable();

  public $currentSearchModel = toSignal(this.currentSearchModel$);
  public $mergedSearchModel = computed(() => this.mergeModels(this.$currentSearchModel(), this.fixedSearchModel));

  private _totalCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public readonly totalCount$ = this._totalCount.asObservable();

  private _currentPage: BehaviorSubject<number> = new BehaviorSubject<number>(1);
  public readonly currentPage = this._currentPage.asObservable();

//   private _currentSort: BehaviorSubject<number> = new BehaviorSubject<any>({});
//   public readonly currentSort$ = this._currentSort.asObservable();

//   private _currentFilters: BehaviorSubject<number> = new BehaviorSubject<any>({});
//   public readonly currentFilters$ = this._currentFilters.asObservable();

  public state = new BehaviorSubject<ListState>(ListStatePending);
  public listId: string = 'general';


  private _alreadySetup: boolean = false;
  private sourceService: TableSource | undefined;
  private defaults: ListDefaults = {};
  private fixedSearchModel: SearchModel = { filter: {}, sort: {} };
  private defaultSearchModel: SearchModel = { filter: {}, sort: {} };
  private searchModel: SearchModel = { filter: {}, sort: {} };
  public pageSize = 10;
  private featuredFilters: string[] = [];
  private quickFilters: string[] = [];
  private hideFilters: string[] = [];
  private preserveState: boolean = false;

  constructor(
    private personalStorage: EASPersonalStorageService
  ) {}

  public setupWith(sourceService: TableSource,
                   defaults: ListDefaults,
                   listId: string): Observable<any> {


    if (this._alreadySetup) {
        this.reset();
    }
    this.sourceService = sourceService;
    this.defaults = defaults;
    this.listId = listId || 'general';
    this.preserveState = !!defaults.preserveState;

    this.fixedSearchModel = cloneDeep(defaults && defaults.fixedSearchModel) || { filter: {}, sort: {}};
    this.defaultSearchModel = cloneDeep(defaults && defaults.searchModel || { filter: {}, sort: {}});
    this.searchModel = this.loadSearchModel() || cloneDeep(this.defaultSearchModel);
    this.featuredFilters = defaults.featuredFilters || [];
    this.quickFilters = defaults.quickFilters || [];
    this.hideFilters = defaults.hideFilters || [];

    this.pageSize = this.searchModel?.pagination?.size || (defaults && defaults.pageSize || 10);
    this._alreadySetup = true;


    return this.loadInitial().pipe(
      switchMap(() => this.load())
    )
  }

  private updateSearchModel() {
    this._currentSearchModel$.next(this.getSearchModel());
  }

  public updateFixedSearchModel(searchModel: SearchModel) {
    this.fixedSearchModel = cloneDeep(searchModel);
  }

  public get mergedSearchModel() {
    // If we want to hide all filters, we do not want to merge them
    // as the searchModel is empty
    if (this.hideFilters.includes('__all__')) {
      const searchModel = this.defaultSearchModel;
      // We don't have filters but we still have sort
      searchModel.sort = {...this.defaultSearchModel.sort || {}, ...this.searchModel.sort || {}};
      return this.mergeModels(this.defaultSearchModel, this.fixedSearchModel);
    }
    return this.mergeModels(this.searchModel, this.fixedSearchModel);
  }

  public mergeModels(modelA: SearchModel | undefined, modelB: SearchModel | undefined): SearchModel {
    // Uf, this is terrible but how else I can force the ts to compile?
    if (modelA === undefined) {
      if (modelB !== undefined) {
        return modelB;
      } else {
        return {
          filter: {},
          sort: {},
        }
      }
    }

    if (modelB === undefined) {
      if (modelA !== undefined) {
        return modelA;
      } else {
        return {
          filter: {},
          sort: {},
        }
      }
    }

    return {
      filter: {...modelA.filter, ...modelB.filter},
      sort: {...modelB.sort, ...modelA.sort},
      select: modelA.select || modelB.select, // We do not want to merge these, we use modelA as default
      options: {...modelA.options, ...modelB.options},
    };
  }

  public reset() {
    this._alreadySetup = false;
  }

  public resetFilter() {
    this._currentPage.next(1);
  }

  public clearFilter() {
    this.searchModel.filter = cloneDeep(this.defaultSearchModel.filter);
  }

  private constructFilter() {
    const searchModel = this.mergedSearchModel;
    const flt: SearchModel = cloneDeep(searchModel)

    if (flt.options === undefined) {
        flt.options = {};
    }

    flt.pagination = {
        start: (this._currentPage.value - 1) * this.pageSize,
        size: this.pageSize,
    }

    flt.context = {
      filter: this.fixedSearchModel.filter,
      user: this.fixedSearchModel.context?.user,
      extra: this.fixedSearchModel.context?.extra,
    };
    return flt;
  }

  public reload() {
    if (!this._alreadySetup) {
        throw new Error('It is not yet setup');
    }

    if (!this.sourceService) {
        throw new Error('Source service is not yet set up');
    }

    this.state.next(ListStateLoading);
    return this.setupWith(this.sourceService,
                          this.defaults,
                          this.listId);
  }

  private loadSearchModel(): SearchModel | undefined {
    if (this.listId === 'general' || !this.preserveState) {
      return undefined;
    }
    return this.personalStorage.getItem(this.listId);
  }

  public saveSearchModel(): void {
    if (this.listId === 'general' || !this.preserveState) {
      return;
    }
    console.log('Storing', this.searchModel);
    this.personalStorage.setItem(this.listId, this.searchModel);
  }

  public loadInitial(): Observable<void> {
    const parts: TablePart[] = ['columns'];
    if (!this.hideFilters.includes('__all__')) {
      parts.push('filters');
    }

    return this._load(parts).pipe(
        map(table => {
            this._tableDefinition.next({
              columns: table.columns,
              filters: table.filters || { filterFormDef: [] }
            })
        })
    )
  }

  public load(): Observable<void> {
    return this._load(["data"]).pipe(
        map(res => {
          this._table.next(res);
          const total = res.meta?.total || 0;
          this._totalCount.next(total);
          this.state.next(total === 0 ? ListStateEmpty : ListStateFull);
        })
    )
  }

  public _load(parts: TablePart[]): Observable<EasTable> {
    if (this.sourceService === undefined) {
        throw new Error("Cannot load the table if not yet set up")
    }
    this.saveSearchModel();
    this.state.next(ListStateLoading);

    const filters = this.constructFilter() as TableRequest;
    filters.includeParts = parts;

    return this.sourceService.table(filters).pipe(
      // tap(res => {
      //   this._table.next(res);
      //   const total = res.meta?.total || 0;
      //   this._totalCount.next(total);
      //   this.state.next(total === 0 ? ListStateEmpty : ListStateFull);
      // }),
      catchError(error => {
        const err = new Error(error);
        this.state.next({ type: 'error', 'error': error });
        throw err;
      })
    );
  }

  public loadExpand(path: string[]) {
    if (this.sourceService === undefined) {
        throw new Error("Cannot load the table if not yet set up")
    }
    const filters = this.constructFilter() as TableRequest;
    filters.filter.path = path;
    // filters.filter.text = undefined;
    filters.includeParts = ['data'];

    return this.sourceService.table(filters).pipe(
      map(res => {
        return res.data;
      }),
      catchError(error => {
        const err = new Error(error);
        this.state.next({ type: 'error', 'error': err });
        throw err;
      })
    );
  }

  public startExport(exportOptions: any): Observable<{ taskId: string }> {
    if (this.sourceService === undefined) {
        throw new Error("Cannot load the table if not yet set up")
    }
    if (this.sourceService.startExport === undefined) {
      throw new Error("Export is not available")
    }
    const filters = this.constructFilter();
    return this.sourceService.startExport(this.listId, filters, exportOptions);
  }

  public setPageSize(pageSize: number) {
    if (pageSize < 0 || pageSize > 1000) {
      throw new Error('Page size has to be within 0 and 1000');
    }
    this.pageSize = pageSize;
    this.searchModel.pagination = {
      size: pageSize,
      start: 0
    };
    this._currentPage.next(1);
    this.updateSearchModel();
  }

  public setSearch(search: SearchModel) {
    this.searchModel = search;
    this.updateSearchModel();
  }

  public setSearchFilter(searchFilter: SearchFilter) {
    this.searchModel.filter = searchFilter;
    this.updateSearchModel();
  }

  public setSearchSort(searchSort: SearchSort) {
    this.searchModel.sort = searchSort;
    this.updateSearchModel();
  }

  public setSearchSelect(searchSelect: SearchSelect[]) {
    this.searchModel.select = searchSelect;
    this.updateSearchModel();
  }

  public setPage(page: number) {
    // Validate the page somehow?
    this._currentPage.next(page);
  }

  public getSearchModel(): SearchModel {
    return cloneDeep(this.searchModel);
  }

  public getFixedModel(): SearchModel {
    return cloneDeep(this.fixedSearchModel);
  }

  public getDefaultSelect(): SearchSelect[] {
    return this.defaults.fixedSearchModel?.select || [];
  }

  public getFiltersFromDef(filterFormDef: any[]): AllowedFilters{
    const filters: AllowedFilters = {
      featured: [],
      normal: [],
      quick: [],
    }
    if (!filterFormDef) {
      return filters;
    }

    filterFormDef.forEach(itm => {
      if (this.hideFilters.includes(itm.id)) {
        return;
      } else if (this.featuredFilters.includes(itm.id)) {
        filters.featured.push(itm);
      } else if (this.quickFilters.includes(itm.id)) {
        filters.quick.push(itm);
      } else {
        filters.normal.push(itm);
      }
    })

    // We should be ignoring the empty fields rather then require them - anyway I don't think we validate it
    // filters.normal.forEach(itm => {
    //   itm.options.required = true;
    // });

    return filters;
  }

  public getTableMeta() {
    return {
      size: this.pageSize,
      start: this._currentPage.value,
      total: this._totalCount.value,
    }
  }
}
